/**
 * In-app guide: a help section with its own secondary nav (left rail on
 * desktop, chip row on mobile) and one stub page per section. The section
 * registry below is the single source of truth — add a section there and it
 * appears in the nav and routes automatically via `/guide/:section?`.
 */

import { BookOpen, Compass, LifeBuoy, type LucideIcon, Sparkles } from "lucide-react";
import { Link, Navigate, useParams } from "react-router";

import { cn } from "@/lib/utils";

interface GuideSection {
  slug: string;
  label: string;
  icon: LucideIcon;
  title: string;
  body: React.ReactNode;
}

/** Shared stub body for sections that haven't been written yet. */
function Stub({ topic }: { topic: string }) {
  return (
    <div className="border-border bg-card rounded-lg border border-dashed p-8 text-center">
      <p className="text-foreground text-sm font-medium">This page is a placeholder.</p>
      <p className="text-muted mt-1 text-sm">
        Write the {topic} content for this app here — screenshots, steps, and talking points.
      </p>
    </div>
  );
}

// TEMPLATE_TODO: replace these placeholder sections with this app's real guide
// (one entry per page; the overview below describes the template itself).
const SECTIONS: GuideSection[] = [
  {
    slug: "overview",
    label: "Overview",
    icon: Compass,
    title: "What this demo is",
    body: (
      <div className="space-y-4">
        <p className="text-foreground text-sm leading-6">
          Vision Deliver Intelligence is the baseline for J&amp;J MedTech Future of Work demo apps:
          J&amp;J branding, sign-in, an admin portal, and the shared demo toolkit (feedback button,
          audience voting, this guide) come pre-wired. App teams add their domain surfaces on top.
        </p>
        <p className="text-muted text-sm leading-6">
          Use this section to orient a first-time viewer: what the demo shows, the story it tells,
          and where to click first.
        </p>
      </div>
    ),
  },
  {
    slug: "walkthrough",
    label: "Walkthrough",
    icon: BookOpen,
    title: "Demo walkthrough",
    body: <Stub topic="step-by-step walkthrough" />,
  },
  {
    slug: "whats-new",
    label: "What's new",
    icon: Sparkles,
    title: "What's new",
    body: <Stub topic="release notes / changelog" />,
  },
  {
    slug: "faq",
    label: "FAQ",
    icon: LifeBuoy,
    title: "Frequently asked questions",
    body: <Stub topic="FAQ" />,
  },
];

export function GuidePage() {
  const { section: slug } = useParams();
  const section = SECTIONS.find((entry) => entry.slug === slug) ?? (slug ? null : SECTIONS[0]);

  // Unknown slugs land on the first section instead of a dead page.
  if (!section) {
    return <Navigate to="/guide" replace />;
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-8 px-6 py-8 md:flex-row">
      {/* Secondary nav: chip row on mobile, left rail on desktop. */}
      <nav aria-label="Guide sections" className="md:w-48 md:shrink-0">
        <ul className="flex flex-wrap gap-1 md:flex-col">
          {SECTIONS.map((entry) => {
            const active = entry.slug === section.slug;
            const Icon = entry.icon;
            return (
              <li key={entry.slug}>
                <Link
                  to={`/guide/${entry.slug}`}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "inline-flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    active
                      ? "bg-accent-light text-foreground"
                      : "text-muted hover:bg-accent-light/60 hover:text-foreground",
                  )}
                >
                  <Icon className={cn("h-4 w-4", active ? "text-primary" : "text-warm-gray-06")} />
                  {entry.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      <article className="min-w-0 flex-1">
        <h2 className="font-heading text-foreground text-2xl font-bold">{section.title}</h2>
        <div className="mt-4">{section.body}</div>
      </article>
    </div>
  );
}
